/**
 * Cliente HTTP tipado para las APIs FastAPI.
 *
 * Responsabilidades (patrón reusable — otras surfaces copian este módulo):
 *   1. Adjunta el JWT (Bearer) a cada request desde el `tokenStore`.
 *   2. Desempaqueta el envelope `{ data, error, meta }` y devuelve `data`.
 *   3. En 401 intenta UN refresh con el refresh token (single-flight) y
 *      reintenta el request original una vez. Si el refresh falla, limpia
 *      la sesión (dispara SIGNED_OUT) y propaga el error.
 *   4. Convierte errores del server en `ApiError` tipado (code + status).
 *
 * Sin dependencias externas (fetch nativo) — mantiene el bundle chico.
 */

import { API_CORE_URL, API_VERSION_PREFIX } from './config';
import { getCachedClientToken } from '../superApiHeartbeat';
import { ApiError, type Envelope, type TokenPair } from './types';
import { clearSession, getAccessToken, getRefreshToken, updateTokens } from './tokenStore';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Cuerpo JSON (se serializa). Omitir para GET/DELETE sin body. */
  body?: unknown;
  /** Query params; los `undefined`/`null` se descartan. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Headers extra (ej. Idempotency-Key). */
  headers?: Record<string, string>;
  /** Si false, no adjunta el Authorization (endpoints públicos). Default true. */
  auth?: boolean;
  /** Si true, devuelve el envelope completo (para leer `meta`). */
  withMeta?: boolean;
}

function buildUrl(baseUrl: string, path: string, query?: RequestOptions['query']): string {
  // `baseUrl` puede ser absoluta (http://...) o relativa (`/api/core`, dev
  // proxy). `new URL` necesita una base absoluta para resolver rutas
  // relativas, así que usamos el origin del documento cuando aplica.
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const full = `${baseUrl}${API_VERSION_PREFIX}${path}`;
  const url = /^https?:\/\//i.test(full) ? new URL(full) : new URL(full, origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function parseEnvelope<T>(res: Response): Promise<Envelope<T>> {
  try {
    return (await res.json()) as Envelope<T>;
  } catch {
    return { data: null, error: null };
  }
}

function errorFromEnvelope(res: Response, env: Envelope<unknown>): ApiError {
  const code = env.error?.code ?? `HTTP_${res.status}`;
  const message = env.error?.message ?? res.statusText ?? 'Error de red';
  const retryable = res.status >= 500 && res.status < 600;
  // `meta` del error viaja al consumer: p. ej. el 409 de la lista de espera
  // trae `meta.position` para pintar la posición sin re-consultar.
  return new ApiError(code, message, res.status, retryable, env.meta);
}

// ---- Refresh single-flight ------------------------------------------------
// Un único refresh en vuelo a la vez: si varios requests reciben 401 a la
// par, todos esperan el mismo refresh en lugar de dispararlos en paralelo.

let refreshInFlight: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${API_CORE_URL}${API_VERSION_PREFIX}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;
    const env = await parseEnvelope<TokenPair>(res);
    if (!env.data?.access_token) return false;
    updateTokens(env.data);
    return true;
  } catch {
    return false;
  }
}

async function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// ---- Core request ---------------------------------------------------------

/**
 * Active UI language → `Accept-Language` (Ola 3 localized backend).
 * Reads the persisted i18n choice from localStorage to avoid an import cycle
 * with the i18n bootstrap; honors es/en, defaults to Spanish.
 */
function acceptLanguage(): string {
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('fc.lang') : null;
    const lang = (stored || (typeof navigator !== 'undefined' ? navigator.language : '') || 'es')
      .toLowerCase()
      .slice(0, 2);
    return lang === 'en' ? 'en' : 'es';
  } catch {
    return 'es';
  }
}

async function doFetch(
  baseUrl: string,
  path: string,
  opts: RequestOptions,
  isRetry: boolean,
): Promise<Response> {
  const useAuth = opts.auth !== false;
  const headers: Record<string, string> = {
    'Accept-Language': acceptLanguage(),
    ...opts.headers,
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (useAuth) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  // Identidad de CLIENTE (Super API) → auditoría por cliente en la zona de
  // peticiones. Convive con el JWT de usuario en Authorization.
  const clientToken = getCachedClientToken();
  if (clientToken && !headers['X-FC-Client-Token']) {
    headers['X-FC-Client-Token'] = clientToken;
  }

  const res = await fetch(buildUrl(baseUrl, path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  // 401 → intentar refresh + reintentar UNA vez.
  if (res.status === 401 && useAuth && !isRetry) {
    const refreshed = await refreshOnce();
    if (refreshed) {
      return doFetch(baseUrl, path, opts, true);
    }
    // Refresh imposible/fallido → cerrar sesión (dispara SIGNED_OUT).
    clearSession();
  }
  return res;
}

/**
 * Ejecuta un request contra una API FastAPI y devuelve `data` desempaquetado.
 * Lanza `ApiError` en fallo. `baseUrl` selecciona core vs taquilla.
 */
export async function apiRequest<T>(
  baseUrl: string,
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const res = await doFetch(baseUrl, path, opts, false);
  const env = await parseEnvelope<T>(res);

  if (!res.ok || env.error) {
    throw errorFromEnvelope(res, env);
  }
  // El envelope envuelve siempre en `data`. Algunos endpoints devuelven
  // `data: null` legítimamente (ej. /memberships/me sin membresía).
  return env.data as T;
}

/**
 * Igual que `apiRequest` pero devuelve el envelope completo, para leer
 * `meta` (paginación). Lanza `ApiError` en fallo.
 */
export async function apiRequestWithMeta<T>(
  baseUrl: string,
  path: string,
  opts: RequestOptions = {},
): Promise<Envelope<T>> {
  const res = await doFetch(baseUrl, path, opts, false);
  const env = await parseEnvelope<T>(res);
  if (!res.ok || env.error) {
    throw errorFromEnvelope(res, env);
  }
  return env;
}
